/**
 * Servicio para manejo de reportes y cierre de caja
 * Simula la lógica de reportes del sistema POS
 */

import { apiClient } from '../core/apiClient';

export interface ShiftSummary {
  totalSales: number;
  cashSales: number;
  cardSales: number;
  creditSales: number;
  transactions: number;
  startTime: string;
  endTime: string;
}

export interface CashClosingReport {
  shiftSummary: ShiftSummary;
  declaredCash: number;
  difference: number;
  timestamp: string;
  userId: string;
}

export interface GlobalKpis {
  todaySales: number;
  monthOrders: number;
  averageTicket: number;
  totalCustomers: number;
}

export interface SalesData {
  date: string;
  sales: number;
  orders: number;
}

export interface CategorySales {
  category: string;
  sales: number;
  percentage: number;
}

// Nuevos tipos para los endpoints del admin dashboard
export interface AdminDashboardKpis {
  totalSalesToday: number;
  totalSalesThisWeek: number;
  totalSalesThisMonth: number;
  averageOrderValue: number;
  totalOrdersToday: number;
  totalOrdersThisWeek: number;
  totalOrdersThisMonth: number;
  ordersInPreparation: number;
  ordersCompleted: number;
  ordersPending: number;
  totalActiveUsers: number;
  newUsersToday: number;
  newUsersThisWeek: number;
  newUsersThisMonth: number;
  totalProducts: number;
  topSellingProducts: TopSellingProduct[];
  totalCashToday: number;
  totalCardToday: number;
  activeCashSessions: number;
  totalQRDeliveriesToday: number;
  totalQRDeliveriesThisWeek: number;
  totalQRDeliveriesThisMonth: number;
  averagePreparationTime: string;
  peakHour: string;
  generatedAt: string;
  period: {
    today: string;
    weekStart: string;
    monthStart: string;
  };
}

export interface TopSellingProduct {
  productId: string;
  productName: string;
  totalSold: number;
  revenue: number;
}

export interface Transaction {
  id: string;
  tipo: string;
  monto: number;
  descripcion: string;
  fecha: string;
  pedidoId: string;
  pedidoQR: string;
  metodoPago: string;
  estadoPago: string;
  montoPagado: number;
  fechaProcesamiento: string;
  idExterno: string;
  cajeroNombre: string;
  clienteNombre: string;
  clienteEmail: string;
  sesionCajaId: string | null;
  movimientoCajaId: string | null;
}

export interface TransactionReport {
  transacciones: Transaction[];
  estadisticas: {
    totalTransacciones: number;
    montoTotal: number;
    montoPorTipo: Record<string, number>;
    montoPorMetodo: Record<string, number>;
    transaccionesExitosas: number;
    transaccionesFallidas: number;
    promedioTransaccion: number;
    transaccionesPorHora: Array<{
      hora: string;
      cantidad: number;
      monto: number;
    }>;
    cajeroMasActivo: string | null;
  };
  filtros: {
    fechaInicio: string;
    fechaFin: string;
  };
  periodo: {
    fechaInicio: string;
    fechaFin: string;
    totalDias: number;
  };
  paginacion: {
    total: number;
    pagina: number;
    limite: number;
    totalPaginas: number;
  };
}

/**
 * Obtiene el resumen del turno actual
 * @returns Promise<ShiftSummary> - Resumen de ventas del turno
 */
export const getShiftSummary = async (): Promise<ShiftSummary> => {
  // Simular delay de red
  await new Promise((resolve) => setTimeout(resolve, 800));

  // Datos simulados del turno
  const now = new Date();
  const startTime = new Date(now.getTime() - 8 * 60 * 60 * 1000); // 8 horas atrás

  return {
    totalSales: 55000,
    cashSales: 25000,
    cardSales: 20000,
    creditSales: 10000,
    transactions: 15,
    startTime: startTime.toISOString(),
    endTime: now.toISOString(),
  };
};

/**
 * Envía el reporte de cierre de caja
 * @param reportData - Datos del reporte de cierre
 * @returns Promise<void>
 */
export const submitShiftReport = async (
  reportData: CashClosingReport
): Promise<void> => {
  // Simular delay de red
  await new Promise((resolve) => setTimeout(resolve, 1200));

  // Simular registro del reporte
  console.log("=== REPORTE DE CIERRE DE CAJA ===");
  console.log("Fecha:", new Date(reportData.timestamp).toLocaleString("es-CL"));
  console.log("Usuario:", reportData.userId);
  console.log("Resumen del Turno:");
  console.log(
    "  - Total Ventas:",
    reportData.shiftSummary.totalSales.toLocaleString("es-CL", {
      style: "currency",
      currency: "CLP",
    })
  );
  console.log(
    "  - Ventas en Efectivo:",
    reportData.shiftSummary.cashSales.toLocaleString("es-CL", {
      style: "currency",
      currency: "CLP",
    })
  );
  console.log(
    "  - Ventas con Tarjeta:",
    reportData.shiftSummary.cardSales.toLocaleString("es-CL", {
      style: "currency",
      currency: "CLP",
    })
  );
  console.log(
    "  - Ventas a Crédito:",
    reportData.shiftSummary.creditSales.toLocaleString("es-CL", {
      style: "currency",
      currency: "CLP",
    })
  );
  console.log("  - Total Transacciones:", reportData.shiftSummary.transactions);
  console.log("Declaración del Cajero:");
  console.log(
    "  - Efectivo Declarado:",
    reportData.declaredCash.toLocaleString("es-CL", {
      style: "currency",
      currency: "CLP",
    })
  );
  console.log(
    "  - Diferencia:",
    reportData.difference.toLocaleString("es-CL", {
      style: "currency",
      currency: "CLP",
    })
  );
  console.log("================================");

  // Simular éxito del envío
  return Promise.resolve();
};

/**
 * Obtiene los KPIs globales del negocio
 * @returns Promise<GlobalKpis> - Indicadores clave de rendimiento
 */
export const getGlobalKpis = async (): Promise<GlobalKpis> => {
  // Simular delay de red
  await new Promise((resolve) => setTimeout(resolve, 600));

  // Datos simulados de KPIs
  return {
    todaySales: 185000,
    monthOrders: 1247,
    averageTicket: 8500,
    totalCustomers: 342,
  };
};

/**
 * Obtiene datos de ventas a lo largo del tiempo
 * @param days - Número de días a obtener (por defecto 30)
 * @returns Promise<SalesData[]> - Array de datos de ventas
 */
export const getSalesOverTime = async (
  days: number = 30
): Promise<SalesData[]> => {
  // Simular delay de red
  await new Promise((resolve) => setTimeout(resolve, 800));

  const salesData: SalesData[] = [];
  const today = new Date();

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);

    // Generar datos simulados con variación realista
    const baseSales = 75000;
    const variation = Math.random() * 0.6 - 0.3; // ±30% variación
    const sales = Math.round(baseSales * (1 + variation));
    const orders = Math.round(sales / (8000 + Math.random() * 4000)); // Ticket variable

    salesData.push({
      date: date.toISOString().split("T")[0],
      sales,
      orders,
    });
  }

  return salesData;
};

/**
 * Obtiene datos de ventas por categoría
 * @returns Promise<CategorySales[]> - Array de ventas por categoría
 */
export const getSalesByCategory = async (): Promise<CategorySales[]> => {
  // Simular delay de red
  await new Promise((resolve) => setTimeout(resolve, 500));

  // Datos simulados por categoría
  const categoryData = [
    { category: "Menús Principales", sales: 450000, percentage: 45 },
    { category: "Bebidas", sales: 280000, percentage: 28 },
    { category: "Snacks", sales: 180000, percentage: 18 },
    { category: "Postres", sales: 90000, percentage: 9 },
  ];

  return categoryData;
};

/**
 * Obtiene los KPIs del dashboard de administrador
 * @returns Promise<AdminDashboardKpis> - KPIs del dashboard
 */
export const getAdminDashboardKpis = async (): Promise<AdminDashboardKpis> => {
  try {
    const response = await apiClient.get('/api/admin/dashboard/kpis');
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener KPIs del dashboard:', error);
    throw error;
  }
};

/**
 * Obtiene el reporte de transacciones
 * @param filters - Filtros opcionales para el reporte
 * @returns Promise<TransactionReport> - Reporte de transacciones
 */
export const getTransactionReport = async (filters?: {
  fechaInicio?: string;
  fechaFin?: string;
  limite?: number;
  pagina?: number;
}): Promise<TransactionReport> => {
  try {
    const params = new URLSearchParams();
    if (filters?.fechaInicio) params.append('fechaInicio', filters.fechaInicio);
    if (filters?.fechaFin) params.append('fechaFin', filters.fechaFin);
    if (filters?.limite) params.append('limite', filters.limite.toString());
    if (filters?.pagina) params.append('pagina', filters.pagina.toString());

    const response = await apiClient.get(`/api/admin/reportes/transacciones?${params.toString()}`);
    return response.data.data;
  } catch (error) {
    console.error('Error al obtener reporte de transacciones:', error);
    throw error;
  }
};
